/* eslint-disable no-bitwise -- a TIFF is bytes, bit fields and codes: bitwise is its arithmetic. */

/**
 * Reading TIFF files, which is what scanners write and what only Safari opens itself. Written
 * here, for the portal's upload and the tracer's evaluation harness alike, with no library.
 *
 * The first page of a baseline TIFF, in strips or tiles: uncompressed, PackBits, LZW, Deflate
 * (given the inflate to use), and the CCITT fax codings scanners use for black-and-white pages —
 * Modified Huffman, Group 3 one- and two-dimensional, and Group 4. Grey, black-and-white, colour
 * with or without alpha, palette and CMYK, 1 to 16 bits a sample, with the horizontal predictor,
 * in any of the eight orientations. Not JPEG inside a TIFF, signed or floating-point samples, or
 * BigTIFF: those say so rather than draw nonsense.
 *
 * A TIFF is a file from anyone, so nothing in it is trusted: sizes are checked before anything is
 * made of that size, every offset against the file's length, and damaged compressed data stops
 * with a `TiffError` rather than a loop that never ends.
 */

export interface TiffPicture {
  readonly width: number;
  readonly height: number;
  /** Four bytes a pixel, red, green, blue and alpha, row by row. */
  readonly pixels: Uint8ClampedArray;
}

/**
 * Decompressing a zlib stream: async, because the browser's own `DecompressionStream` is.
 * `expected` is how many bytes the chunk should inflate to; an implementation must not give more
 * than that, stopping or throwing once it would. Any more that comes back is cut off.
 */
export type TiffInflate = (data: Uint8Array, expected: number) => Promise<Uint8Array>;

export interface TiffOptions {
  /** Called once each strip or tile is decoded and every so many rows after: a sign of life for a worker to pass on. */
  readonly onProgress?: () => void;
  /** The most pixels a picture may have, `TIFF_MAX_PIXELS` unless said. */
  readonly maxPixels?: number;
}

/** The most pixels a TIFF may have to be read: 50 million, an A0 plan scanned at 300 dpi and then some. */
export const TIFF_MAX_PIXELS = 50_000_000;

/**
 * The most memory a TIFF may take to read: its samples as stored plus the RGBA picture they make,
 * 600 MB. A 50-million-pixel RGB scan takes 350 MB; the same with 16-bit samples and alpha, 600 MB.
 */
export const TIFF_MAX_BYTES = 600_000_000;

/** The largest a tile may be on a side, and the most bytes one may hold beyond what the picture itself does. */
const MAX_TILE_SIDE = 65_536;
const MAX_TILE_BYTES = 1 << 24;

/** Why a TIFF could not be read, in words to show. */
export class TiffError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TiffError';
  }
}

/** Whether `bytes` start as a TIFF does: "II*\0" or "MM\0*", or as a BigTIFF with 43 for 42. */
export const isTiff = (bytes: Uint8Array): boolean => bytes.length >= 8
  && ((bytes[0] === 0x49 && bytes[1] === 0x49 && (bytes[2] === 42 || bytes[2] === 43) && bytes[3] === 0)
    || (bytes[0] === 0x4d && bytes[1] === 0x4d && bytes[2] === 0 && (bytes[3] === 42 || bytes[3] === 43)));

const TAG = {
  width: 256,
  height: 257,
  bitsPerSample: 258,
  compression: 259,
  photometric: 262,
  fillOrder: 266,
  stripOffsets: 273,
  orientation: 274,
  samplesPerPixel: 277,
  rowsPerStrip: 278,
  stripByteCounts: 279,
  planar: 284,
  t4Options: 292,
  predictor: 317,
  colorMap: 320,
  tileWidth: 322,
  tileLength: 323,
  tileOffsets: 324,
  tileByteCounts: 325,
  extraSamples: 338,
  sampleFormat: 339,
} as const;

/** Bytes a value of each TIFF field type takes. */
const TYPE_SIZE: Record<number, number> = {
  1: 1, 2: 1, 3: 2, 4: 4, 5: 8, 6: 1, 7: 1, 8: 2, 9: 4, 10: 8, 11: 4, 12: 8, 13: 4,
};

/** The first page's fields, each as the list of numbers it holds. */
const fieldsOf = (bytes: Uint8Array): Map<number, number[]> => {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const little = bytes[0] === 0x49;
  const u16 = (at: number): number => view.getUint16(at, little);
  const u32 = (at: number): number => view.getUint32(at, little);

  if (u16(2) === 43) throw new TiffError('BigTIFF files are not supported: save it as an ordinary TIFF or PNG');
  const directory = u32(4);
  if (directory + 2 > bytes.length) throw new TiffError('the TIFF file is cut short');

  const fields = new Map<number, number[]>();
  const count = u16(directory);
  if (directory + 2 + (count * 12) > bytes.length) throw new TiffError('the TIFF file is cut short');

  for (let entry = 0; entry < count; entry += 1) {
    const at = directory + 2 + (entry * 12);
    const tag = u16(at);
    const type = u16(at + 2);
    const values = u32(at + 4);
    const size = TYPE_SIZE[type];
    if (size) {
      const inline = size * values <= 4;
      const start = inline ? at + 8 : u32(at + 8);
      const read = (index: number): number => {
        const place = start + (index * size);
        switch (type) {
          case 3: return u16(place);
          case 4: case 13: return u32(place);
          case 8: return view.getInt16(place, little);
          case 9: return view.getInt32(place, little);
          case 5: return u32(place) / Math.max(1, u32(place + 4));
          case 10: return view.getInt32(place, little) / Math.max(1, view.getInt32(place + 4, little));
          case 11: return view.getFloat32(place, little);
          case 12: return view.getFloat64(place, little);
          case 6: return view.getInt8(place);
          default: return bytes[place];
        }
      };
      if (start + (size * values) <= bytes.length) fields.set(tag, Array.from({ length: values }, (_, index) => read(index)));
    }
  }

  return fields;
};

// --------------------------------------------------------------------------- PackBits, LZW

const unpackBits = (data: Uint8Array, expected: number): Uint8Array => {
  const out = new Uint8Array(expected);
  let [from, to] = [0, 0];

  while (from < data.length && to < expected) {
    const header = (data[from] << 24) >> 24;
    from += 1;
    if (header >= 0) {
      const length = Math.min(header + 1, expected - to);
      out.set(data.subarray(from, from + length), to);
      from += header + 1;
      to += length;
    } else if (header !== -128) {
      const length = Math.min(1 - header, expected - to);
      out.fill(data[from], to, to + length);
      from += 1;
      to += length;
    }
  }

  return out;
};

const LZW_CLEAR = 256;
const LZW_END = 257;
const LZW_CODES = 4096;

/**
 * TIFF's LZW: codes read most significant bit first, widening a code early, as TIFF writers do.
 * Every code is checked against the table as it stands, so a damaged strip is an error, never a
 * table that points back at itself.
 */
const unLzw = (data: Uint8Array, expected: number): Uint8Array => {
  if (data[0] === 0 && data[1] === 1) throw new TiffError('old-style LZW TIFF files are not supported: save it again');
  const damaged = (): TiffError => new TiffError('the LZW-compressed TIFF data is damaged');

  const out = new Uint8Array(expected);
  // Each code as the code before it, its last byte, its first byte and its length: an entry's
  // prefix is always an earlier code, so walking back from one always ends.
  const prefix = new Int32Array(LZW_CODES);
  const suffix = new Uint8Array(LZW_CODES);
  const firsts = new Uint8Array(LZW_CODES);
  const lengths = new Uint16Array(LZW_CODES);
  for (let code = 0; code < 256; code += 1) {
    suffix[code] = code;
    firsts[code] = code;
    lengths[code] = 1;
    prefix[code] = -1;
  }

  const totalBits = data.length * 8;
  let written = 0;
  let bit = 0;
  let width = 9;
  let next = 258;
  let previous = -1;

  const readCode = (): number => {
    // Three bytes hold any code of up to twelve bits, wherever it starts; past the end reads as 0.
    const at = bit >> 3;
    const shift = 24 - width - (bit & 7);
    bit += width;
    return ((((data[at] << 16) | (data[at + 1] << 8) | data[at + 2]) >> shift) & ((1 << width) - 1));
  };

  // A code's bytes, written backwards from its last, where it goes.
  const emit = (code: number): void => {
    const length = lengths[code];
    let place = written + length - 1;
    let walk = code;
    for (let step = 0; step < length && walk >= 0; step += 1) {
      if (place < expected) out[place] = suffix[walk];
      place -= 1;
      walk = prefix[walk];
    }
    written += length;
  };

  while (written < expected && bit + width <= totalBits) {
    const code = readCode();
    if (code === LZW_END) break;
    if (code === LZW_CLEAR) {
      width = 9;
      next = 258;
      previous = -1;
    } else if (previous < 0) {
      // The first code after a clear can only be a byte: there is nothing else in the table yet.
      if (code > 255) throw damaged();
      emit(code);
      previous = code;
    } else {
      // A code already in the table, or the one about to be: anything further on is damage.
      if (code > next || (code === next && next >= LZW_CODES)) throw damaged();
      const first = code < next ? firsts[code] : firsts[previous];
      if (next < LZW_CODES) {
        prefix[next] = previous;
        suffix[next] = first;
        firsts[next] = firsts[previous];
        lengths[next] = lengths[previous] + 1;
        next += 1;
      }
      emit(code);
      previous = code;
      if (next + 1 >= (1 << width) && width < 12) width += 1;
    }
  }

  return out;
};

// ------------------------------------------------------------------------------ CCITT fax

/** The run-length codes of ITU-T T.4, as run length and code bits: terminating codes and make-up codes. */
const WHITE_CODES: [number, string][] = [
  [0, '00110101'], [1, '000111'], [2, '0111'], [3, '1000'], [4, '1011'], [5, '1100'], [6, '1110'], [7, '1111'],
  [8, '10011'], [9, '10100'], [10, '00111'], [11, '01000'], [12, '001000'], [13, '000011'], [14, '110100'], [15, '110101'],
  [16, '101010'], [17, '101011'], [18, '0100111'], [19, '0001100'], [20, '0001000'], [21, '0010111'], [22, '0000011'],
  [23, '0000100'], [24, '0101000'], [25, '0101011'], [26, '0010011'], [27, '0100100'], [28, '0011000'], [29, '00000010'],
  [30, '00000011'], [31, '00011010'], [32, '00011011'], [33, '00010010'], [34, '00010011'], [35, '00010100'],
  [36, '00010101'], [37, '00010110'], [38, '00010111'], [39, '00101000'], [40, '00101001'], [41, '00101010'],
  [42, '00101011'], [43, '00101100'], [44, '00101101'], [45, '00000100'], [46, '00000101'], [47, '00001010'],
  [48, '00001011'], [49, '01010010'], [50, '01010011'], [51, '01010100'], [52, '01010101'], [53, '00100100'],
  [54, '00100101'], [55, '01011000'], [56, '01011001'], [57, '01011010'], [58, '01011011'], [59, '01001010'],
  [60, '01001011'], [61, '00110010'], [62, '00110011'], [63, '00110100'],
  [64, '11011'], [128, '10010'], [192, '010111'], [256, '0110111'], [320, '00110110'], [384, '00110111'],
  [448, '01100100'], [512, '01100101'], [576, '01101000'], [640, '01100111'], [704, '011001100'], [768, '011001101'],
  [832, '011010010'], [896, '011010011'], [960, '011010100'], [1024, '011010101'], [1088, '011010110'],
  [1152, '011010111'], [1216, '011011000'], [1280, '011011001'], [1344, '011011010'], [1408, '011011011'],
  [1472, '010011000'], [1536, '010011001'], [1600, '010011010'], [1664, '011000'], [1728, '010011011'],
];

const BLACK_CODES: [number, string][] = [
  [0, '0000110111'], [1, '010'], [2, '11'], [3, '10'], [4, '011'], [5, '0011'], [6, '0010'], [7, '00011'],
  [8, '000101'], [9, '000100'], [10, '0000100'], [11, '0000101'], [12, '0000111'], [13, '00000100'], [14, '00000111'],
  [15, '000011000'], [16, '0000010111'], [17, '0000011000'], [18, '0000001000'], [19, '00001100111'],
  [20, '00001101000'], [21, '00001101100'], [22, '00000110111'], [23, '00000101000'], [24, '00000010111'],
  [25, '00000011000'], [26, '000011001010'], [27, '000011001011'], [28, '000011001100'], [29, '000011001101'],
  [30, '000001101000'], [31, '000001101001'], [32, '000001101010'], [33, '000001101011'], [34, '000011010010'],
  [35, '000011010011'], [36, '000011010100'], [37, '000011010101'], [38, '000011010110'], [39, '000011010111'],
  [40, '000001101100'], [41, '000001101101'], [42, '000011011010'], [43, '000011011011'], [44, '000001010100'],
  [45, '000001010101'], [46, '000001010110'], [47, '000001010111'], [48, '000001100100'], [49, '000001100101'],
  [50, '000001010010'], [51, '000001010011'], [52, '000000100100'], [53, '000000110111'], [54, '000000111000'],
  [55, '000000100111'], [56, '000000101000'], [57, '000001011000'], [58, '000001011001'], [59, '000000101011'],
  [60, '000000101100'], [61, '000001011010'], [62, '000001100110'], [63, '000001100111'],
  [64, '0000001111'], [128, '000011001000'], [192, '000011001001'], [256, '000001011011'], [320, '000000110011'],
  [384, '000000110100'], [448, '000000110101'], [512, '0000001101100'], [576, '0000001101101'], [640, '0000001001010'],
  [704, '0000001001011'], [768, '0000001001100'], [832, '0000001001101'], [896, '0000001110010'],
  [960, '0000001110011'], [1024, '0000001110100'], [1088, '0000001110101'], [1152, '0000001110110'],
  [1216, '0000001110111'], [1280, '0000001010010'], [1344, '0000001010011'], [1408, '0000001010100'],
  [1472, '0000001010101'], [1536, '0000001011010'], [1600, '0000001011011'], [1664, '0000001100100'],
  [1728, '0000001100101'],
];

/** Make-up codes for runs longer than 1,728: the same for both colours. */
const EXTENDED_CODES: [number, string][] = [
  [1792, '00000001000'], [1856, '00000001100'], [1920, '00000001101'], [1984, '000000010010'], [2048, '000000010011'],
  [2112, '000000010100'], [2176, '000000010101'], [2240, '000000010110'], [2304, '000000010111'],
  [2368, '000000011100'], [2432, '000000011101'], [2496, '000000011110'], [2560, '000000011111'],
];

/** Codes to runs, keyed by the code's length and bits together. */
const tableOf = (codes: readonly [number, string][]): Map<number, number> => new Map(
  [...codes, ...EXTENDED_CODES].map(([run, code]) => [(code.length << 16) | parseInt(code, 2), run]),
);

let tables: { white: Map<number, number>; black: Map<number, number> } | undefined;
const faxTables = (): { white: Map<number, number>; black: Map<number, number> } => {
  tables ??= { white: tableOf(WHITE_CODES), black: tableOf(BLACK_CODES) };
  return tables;
};

/** The two-dimensional modes: pass, horizontal, and vertical by -3 to 3, keyed as the run codes are. */
const MODES = new Map<number, string>([
  [(4 << 16) | 0b0001, 'pass'], [(3 << 16) | 0b001, 'horizontal'], [(1 << 16) | 0b1, 'v0'],
  [(3 << 16) | 0b011, 'r1'], [(3 << 16) | 0b010, 'l1'], [(6 << 16) | 0b000011, 'r2'], [(6 << 16) | 0b000010, 'l2'],
  [(7 << 16) | 0b0000011, 'r3'], [(7 << 16) | 0b0000010, 'l3'],
]);
const VERTICAL: Record<string, number> = {
  v0: 0, r1: 1, l1: -1, r2: 2, l2: -2, r3: 3, l3: -3,
};

/**
 * A fax-coded strip decoded into packed rows, a bit a pixel, 1 for black: `coding` 2 is Modified
 * Huffman (each row a byte apart), 3 Group 3 (with `t4Options` saying whether rows are coded
 * against the one above), 4 Group 4.
 */
const unFax = (data: Uint8Array, width: number, rows: number, coding: number, t4Options: number): Uint8Array => {
  const rowBytes = Math.ceil(width / 8);
  const out = new Uint8Array(rowBytes * rows);
  const { white, black } = faxTables();
  const totalBits = data.length * 8;
  let bit = 0;

  const peekBit = (): number => (bit < totalBits ? (data[bit >> 3] >> (7 - (bit & 7))) & 1 : 0);
  const readBit = (): number => {
    const value = peekBit();
    bit += 1;
    return value;
  };

  /** The next code found in `table`, read a bit at a time; undefined at the end of the data. */
  const readCode = <T>(table: Map<number, T>, longest: number): T | undefined => {
    let code = 0;
    for (let length = 1; length <= longest && bit < totalBits; length += 1) {
      code = (code << 1) | readBit();
      const found = table.get((length << 16) | code);
      if (found !== undefined) return found;
    }
    return undefined;
  };

  /** A whole run of one colour: make-up codes, then the terminating one. */
  const readRun = (isWhite: boolean): number => {
    let total = 0;
    for (;;) {
      const run = readCode(isWhite ? white : black, 13);
      if (run === undefined) throw new TiffError('the fax-coded TIFF data is damaged');
      total += run;
      if (run < 64) return total;
    }
  };

  /** Past an end-of-line code, if one is next: eleven or more zeros, then a one. */
  const skipEndOfLine = (): boolean => {
    let zeros = 0;
    const start = bit;
    while (bit < totalBits && peekBit() === 0) {
      bit += 1;
      zeros += 1;
    }
    if (zeros >= 11 && bit < totalBits) {
      bit += 1;
      return true;
    }
    bit = start;
    return false;
  };

  const paint = (row: number, from: number, to: number): void => {
    for (let x = Math.max(0, from); x < Math.min(width, to); x += 1) out[(row * rowBytes) + (x >> 3)] |= 0x80 >> (x & 7);
  };

  /** A row coded on its own: runs of white and black in turn, from white. */
  const oneDimensional = (row: number): number[] => {
    const changes: number[] = [];
    let at = 0;
    let isWhite = true;
    while (at < width) {
      const run = readRun(isWhite);
      if (!isWhite) paint(row, at, at + run);
      at += run;
      changes.push(Math.min(at, width));
      isWhite = !isWhite;
    }
    return changes;
  };

  /** A row coded against the row above it, whose colour changes are `reference`. */
  const twoDimensional = (row: number, reference: readonly number[]): number[] => {
    const changes: number[] = [];
    let a0 = -1;
    let isWhite = true;
    const ref = [...reference, width, width];

    // The first change above, right of a0, to the colour a0 is not.
    const b1Of = (): number => {
      let index = isWhite ? 0 : 1;
      while (index < ref.length && ref[index] <= a0) index += 2;
      return index;
    };

    while (a0 < width) {
      const mode = readCode(MODES, 7);
      if (mode === undefined) throw new TiffError('the fax-coded TIFF data is damaged');
      const index = b1Of();
      const b1 = ref[Math.min(index, ref.length - 1)];
      const b2 = ref[Math.min(index + 1, ref.length - 1)];
      const start = Math.max(0, a0);

      if (mode === 'pass') {
        if (!isWhite) paint(row, start, b2);
        a0 = b2;
      } else if (mode === 'horizontal') {
        const first = readRun(isWhite);
        const second = readRun(!isWhite);
        const a1 = start + first;
        const a2 = a1 + second;
        paint(row, isWhite ? a1 : start, isWhite ? a2 : a1);
        changes.push(Math.min(a1, width), Math.min(a2, width));
        a0 = a2;
      } else {
        const a1 = b1 + VERTICAL[mode];
        if (!isWhite) paint(row, start, a1);
        changes.push(Math.min(Math.max(a1, 0), width));
        a0 = a1;
        isWhite = !isWhite;
      }
    }

    return changes;
  };

  const twoD = coding === 4 || (coding === 3 && (t4Options & 1) === 1);
  let reference: number[] = [];

  for (let row = 0; row < rows && bit < totalBits; row += 1) {
    if (coding === 2 && row > 0) bit = Math.ceil(bit / 8) * 8;
    if (coding === 3) {
      // Fill bits may pad each end of line out to a byte, and the first line may start with one.
      skipEndOfLine();
    }
    let useTwoD = twoD;
    if (coding === 3 && twoD) useTwoD = readBit() === 0;
    reference = useTwoD ? twoDimensional(row, reference) : oneDimensional(row);
  }

  return out;
};

// ------------------------------------------------------------------------------- decoding

/** How many strips or tiles a TIFF may be short of what its size needs, the missing ones left blank. */
const CHUNK_SLACK = 16;

let reversedBytes: Uint8Array | undefined;

/** Each byte's bits the other way round: for fill order 2, least significant bit first. */
const reversed = (data: Uint8Array): Uint8Array => {
  if (!reversedBytes) {
    reversedBytes = new Uint8Array(256);
    for (let byte = 0; byte < 256; byte += 1) {
      let turned = 0;
      for (let bit = 0; bit < 8; bit += 1) turned |= ((byte >> bit) & 1) << (7 - bit);
      reversedBytes[byte] = turned;
    }
  }
  const table = reversedBytes;
  const out = new Uint8Array(data.length);
  for (let index = 0; index < data.length; index += 1) out[index] = table[data[index]];
  return out;
};

/**
 * Where the stored pixels go for the picture to be seen the way its orientation says: pixel x of
 * stored row y goes to pixel `start(y) + (x * step)` of a picture `width` by `height`.
 */
interface Placement {
  readonly width: number;
  readonly height: number;
  readonly start: (y: number) => number;
  readonly step: number;
}

const placementOf = (width: number, height: number, orientation: number): Placement => {
  switch (orientation) {
    case 2: return {
      width, height, start: (y) => (y * width) + width - 1, step: -1,
    };
    case 3: return {
      width, height, start: (y) => ((height - 1 - y) * width) + width - 1, step: -1,
    };
    case 4: return {
      width, height, start: (y) => (height - 1 - y) * width, step: 1,
    };
    // Stored on its side: each stored row becomes a column.
    case 5: return {
      width: height, height: width, start: (y) => y, step: height,
    };
    case 6: return {
      width: height, height: width, start: (y) => height - 1 - y, step: height,
    };
    case 7: return {
      width: height, height: width, start: (y) => ((width - 1) * height) + height - 1 - y, step: -height,
    };
    case 8: return {
      width: height, height: width, start: (y) => ((width - 1) * height) + y, step: -height,
    };
    default: return {
      width, height, start: (y) => y * width, step: 1,
    };
  }
};

/** Rows coloured at a time before they go to their places in the picture. */
const BAND_ROWS = 32;

/** Whether `value` is a whole number from 1 to `most`. */
const isSize = (value: number, most: number): boolean => Number.isInteger(value) && value >= 1 && value <= most;

/**
 * The first page of a TIFF file, as RGBA, turned the way its orientation says. Throws a
 * `TiffError` saying why when it cannot, a picture too large to read among the reasons.
 */
export const decodeTiff = async (bytes: Uint8Array, inflate: TiffInflate, options: TiffOptions = {}): Promise<TiffPicture> => {
  if (!isTiff(bytes)) throw new TiffError('not a TIFF file');
  const { onProgress, maxPixels = TIFF_MAX_PIXELS } = options;
  const progress = (): void => onProgress?.();
  const fields = fieldsOf(bytes);
  const one = (tag: number, fallback: number): number => fields.get(tag)?.[0] ?? fallback;

  const width = one(TAG.width, 0);
  const height = one(TAG.height, 0);
  if (!isSize(width, Number.MAX_SAFE_INTEGER) || !isSize(height, Number.MAX_SAFE_INTEGER)) throw new TiffError('the TIFF has no size');
  if (width * height > maxPixels) throw new TiffError('the TIFF is too large to read in the browser');

  const samples = one(TAG.samplesPerPixel, 1);
  const allBits = fields.get(TAG.bitsPerSample) ?? [1];
  const bits = allBits[0];
  const compression = one(TAG.compression, 1);
  const photometric = one(TAG.photometric, samples >= 3 ? 2 : 1);
  const planar = one(TAG.planar, 1);
  const predictor = one(TAG.predictor, 1);
  const fillOrder = one(TAG.fillOrder, 1);
  const t4Options = one(TAG.t4Options, 0);
  const sampleFormats = fields.get(TAG.sampleFormat) ?? [1];

  if (compression === 6 || compression === 7) throw new TiffError('JPEG-compressed TIFF files are not supported: save it as PNG or JPEG');
  if (![1, 2, 3, 4, 5, 8, 32773, 32946].includes(compression)) throw new TiffError(`TIFF compression ${compression} is not supported`);
  if (sampleFormats.includes(3)) throw new TiffError('floating-point TIFF files are not supported: save it as PNG or JPEG');
  if (sampleFormats.includes(2)) throw new TiffError('TIFF files with signed samples are not supported: save it as PNG or JPEG');
  if (![1, 2, 4, 8, 16].includes(bits)) throw new TiffError(`${bits}-bit TIFF samples are not supported`);
  if (allBits.some((each) => each !== bits)) throw new TiffError('TIFF samples of mixed sizes are not supported: save it as PNG');
  if (![0, 1, 2, 3, 5].includes(photometric)) throw new TiffError('this TIFF colour space is not supported: save it as PNG or JPEG');
  if (predictor !== 1 && predictor !== 2) throw new TiffError(`TIFF predictor ${predictor} is not supported: save it as PNG or JPEG`);

  // The samples that make a colour: red, green and blue; cyan, magenta, yellow and black; or one.
  let colourSamples = 1;
  if (photometric === 2) colourSamples = 3;
  if (photometric === 5) colourSamples = 4;
  if (!isSize(samples, 64) || samples < colourSamples) throw new TiffError('the TIFF has too few samples a pixel for its colours');

  const tiled = fields.has(TAG.tileOffsets);
  const chunkWidth = tiled ? one(TAG.tileWidth, width) : width;
  const chunkHeight = tiled ? one(TAG.tileLength, height) : Math.min(height, one(TAG.rowsPerStrip, height));
  if (!isSize(chunkWidth, tiled ? MAX_TILE_SIDE : width) || !isSize(chunkHeight, tiled ? MAX_TILE_SIDE : height)) {
    throw new TiffError(tiled ? 'the tiles of the TIFF have no proper size' : 'the strips of the TIFF have no proper size');
  }
  const offsets = fields.get(tiled ? TAG.tileOffsets : TAG.stripOffsets) ?? [];
  const counts = fields.get(tiled ? TAG.tileByteCounts : TAG.stripByteCounts) ?? [];
  if (!offsets.length) throw new TiffError('the TIFF has no picture data');

  // Samples in each chunk's pixel: all of them together, or one plane at a time.
  const perPixel = planar === 2 ? 1 : samples;
  const chunkRowBytes = Math.ceil((chunkWidth * perPixel * bits) / 8);
  const across = Math.ceil(width / chunkWidth);
  const down = Math.ceil(height / chunkHeight);
  const planes = planar === 2 ? samples : 1;
  const rowBytes = Math.ceil((width * perPixel * bits) / 8);
  const planeBytes = rowBytes * height;

  if (across * down * planes > offsets.length + CHUNK_SLACK) throw new TiffError('the TIFF file is missing strips or tiles');
  if ((planeBytes * planes) + (width * height * 4) > TIFF_MAX_BYTES) throw new TiffError('the TIFF is too large to read in the browser');
  if (tiled && chunkRowBytes * chunkHeight > Math.max(planeBytes, MAX_TILE_BYTES)) {
    throw new TiffError('the tiles of the TIFF are too large to read');
  }

  const raster = Array.from({ length: planes }, () => new Uint8Array(planeBytes));
  const little = bytes[0] === 0x49;
  let anyData = false;

  for (let plane = 0; plane < planes; plane += 1) {
    for (let chunkY = 0; chunkY < down; chunkY += 1) {
      for (let chunkX = 0; chunkX < across; chunkX += 1) {
        const index = (plane * across * down) + (chunkY * across) + chunkX;
        const offset = offsets[index];
        // A chunk the file stops before is left blank: the file is cut short there.
        if (offset !== undefined && offset < bytes.length) {
          anyData = true;
          const rows = tiled ? chunkHeight : Math.min(chunkHeight, height - (chunkY * chunkHeight));
          const expected = chunkRowBytes * rows;
          const count = counts[index] ?? (compression === 1 ? expected : bytes.length - offset);
          let data = bytes.subarray(offset, offset + count);
          if (fillOrder === 2) data = reversed(data);

          let raw: Uint8Array;
          // Uncompressed data is the caller's own bytes: copied before the predictor changes it.
          if (compression === 1) raw = predictor === 2 && fillOrder !== 2 ? data.slice(0, expected) : data;
          else if (compression === 32773) raw = unpackBits(data, expected);
          else if (compression === 5) raw = unLzw(data, expected);
          else if (compression === 8 || compression === 32946) {
            // eslint-disable-next-line no-await-in-loop -- one chunk after another, into one picture
            raw = await inflate(data, expected);
            if (raw.length > expected) raw = raw.subarray(0, expected);
          } else raw = unFax(data, chunkWidth, rows, compression, t4Options);

          if (predictor === 2) {
            for (let row = 0; row < rows; row += 1) {
              const line = raw.subarray(row * chunkRowBytes, (row + 1) * chunkRowBytes);
              if (bits === 8) {
                for (let x = perPixel; x < line.length; x += 1) line[x] = (line[x] + line[x - perPixel]) & 0xff;
              } else if (bits === 16) {
                const view = new DataView(line.buffer, line.byteOffset, line.byteLength);
                for (let x = perPixel * 2; x + 1 < line.length; x += 2) {
                  view.setUint16(x, (view.getUint16(x, little) + view.getUint16(x - (perPixel * 2), little)) & 0xffff, little);
                }
              }
            }
          }

          // The chunk into its place in the whole picture, row by row.
          const left = chunkX * chunkWidth;
          const copyBits = Math.min(chunkWidth, width - left) * perPixel * bits;
          const target = raster[plane];
          const startBit = left * perPixel * bits;
          for (let row = 0; row < rows && (chunkY * chunkHeight) + row < height; row += 1) {
            const y = (chunkY * chunkHeight) + row;
            if (startBit % 8 === 0) {
              const source = raw.subarray(row * chunkRowBytes, (row * chunkRowBytes) + Math.ceil(copyBits / 8));
              target.set(source, (y * rowBytes) + (startBit / 8));
            } else {
              for (let b = 0; b < copyBits; b += 1) {
                const value = (raw[(row * chunkRowBytes) + (b >> 3)] >> (7 - (b & 7))) & 1;
                const place = startBit + b;
                if (value) target[(y * rowBytes) + (place >> 3)] |= 0x80 >> (place & 7);
              }
            }
          }
          progress();
        }
      }
    }
  }
  if (!anyData) throw new TiffError('the TIFF file is cut short');

  // Samples to colours, a row at a time: each sample of a row read out as 0 to 255, then the
  // colours made of them written straight to where the orientation puts them.
  const maximum = (2 ** bits) - 1;
  const scaled = bits < 8 ? Uint8Array.from({ length: maximum + 1 }, (_, value) => Math.round((value * 255) / maximum)) : undefined;
  const highByte = little ? 1 : 0;

  /** Sample `channel` of every pixel of row `y`, as 0 to 255 or, `raw`, as the number stored. */
  const readRow = (channel: number, y: number, into: Uint8Array | Uint16Array, raw = false): void => {
    const data = raster[planar === 2 ? channel : 0];
    const stride = planar === 2 ? 1 : samples;
    const first = planar === 2 ? 0 : channel;
    const line = y * rowBytes;
    const target = into;
    if (bits === 8) {
      for (let x = 0, at = line + first; x < width; x += 1, at += stride) target[x] = data[at];
    } else if (bits === 16 && raw) {
      for (let x = 0, at = line + (first * 2); x < width; x += 1, at += stride * 2) {
        target[x] = little ? data[at] | (data[at + 1] << 8) : (data[at] << 8) | data[at + 1];
      }
    } else if (bits === 16) {
      for (let x = 0, at = line + (first * 2) + highByte; x < width; x += 1, at += stride * 2) target[x] = data[at];
    } else {
      for (let x = 0, bit = first * bits; x < width; x += 1, bit += stride * bits) {
        const value = (data[line + (bit >> 3)] >> (8 - bits - (bit & 7))) & maximum;
        target[x] = raw || !scaled ? value : scaled[value];
      }
    }
  };

  // Alpha is an extra sample said to be one: associated (colours premultiplied by it) or not.
  const extraSamples = fields.get(TAG.extraSamples) ?? [];
  const alphaIndex = extraSamples.findIndex((kind) => kind === 1 || kind === 2);
  const alphaChannel = alphaIndex >= 0 && colourSamples + alphaIndex < samples ? colourSamples + alphaIndex : -1;
  const premultiplied = alphaChannel >= 0 && extraSamples[alphaIndex] === 1;

  // Rows are coloured into a band of rows as they are stored, then the band put where the
  // orientation says: a picture stored on its side is written a band's width of pixels at a time,
  // not one pixel a row apart.
  const placement = placementOf(width, height, one(TAG.orientation, 1));
  const { step } = placement;
  const pixels = new Uint8ClampedArray(width * height * 4);
  const words = new Uint32Array(pixels.buffer);
  const bandRows = Math.min(height, BAND_ROWS);
  const band = new Uint8ClampedArray(width * bandRows * 4);
  const bandWords = new Uint32Array(band.buffer);
  const starts = new Int32Array(bandRows);
  const channels = Array.from({ length: colourSamples }, () => new Uint8Array(width));
  const alphas = new Uint8Array(width).fill(255);

  /** The band's first `rows` rows, stored row `first` the top one, into their places in the picture. */
  const placeBand = (first: number, rows: number): void => {
    for (let row = 0; row < rows; row += 1) starts[row] = placement.start(first + row);
    if (step === 1) {
      for (let row = 0; row < rows; row += 1) words.set(bandWords.subarray(row * width, (row + 1) * width), starts[row]);
    } else if (step === -1) {
      for (let row = 0; row < rows; row += 1) {
        for (let x = 0, from = row * width, to = starts[row]; x < width; x += 1, from += 1, to -= 1) words[to] = bandWords[from];
      }
    } else {
      for (let x = 0, offset = 0; x < width; x += 1, offset += step) {
        for (let row = 0, from = x; row < rows; row += 1, from += width) words[starts[row] + offset] = bandWords[from];
      }
    }
  };

  // A grey pixel as the four bytes it is, made byte by byte so a word holds them in memory order on any machine.
  const greys = new Uint32Array(256);
  const greyBytes = new Uint8Array(greys.buffer);
  for (let value = 0; value < 256; value += 1) greyBytes.set([value, value, value, 255], value * 4);

  /** One pixel of the band, un-premultiplied when its alpha is associated. */
  const put = (at: number, r: number, g: number, b: number, a: number): void => {
    const place = at * 4;
    if (premultiplied) {
      band[place] = a ? (r * 255) / a : 0;
      band[place + 1] = a ? (g * 255) / a : 0;
      band[place + 2] = a ? (b * 255) / a : 0;
    } else {
      band[place] = r;
      band[place + 1] = g;
      band[place + 2] = b;
    }
    band[place + 3] = a;
  };

  // Stored row y into the band from pixel `base`: chosen once, for the kind of colour the TIFF holds.
  let colourRow: (y: number, base: number) => void;
  const colorMap = fields.get(TAG.colorMap);
  if (photometric === 3 && colorMap) {
    const size = colorMap.length / 3;
    const entries = maximum + 1;
    const [reds, greens, blues] = [new Uint8Array(entries), new Uint8Array(entries), new Uint8Array(entries)];
    for (let entry = 0; entry < entries; entry += 1) {
      [reds[entry], greens[entry], blues[entry]] = [colorMap[entry] >> 8, colorMap[size + entry] >> 8, colorMap[(2 * size) + entry] >> 8];
    }
    const indexes = new Uint16Array(width);
    colourRow = (y, base): void => {
      readRow(0, y, indexes, true);
      if (alphaChannel >= 0) readRow(alphaChannel, y, alphas);
      for (let x = 0; x < width; x += 1) {
        const entry = indexes[x];
        put(base + x, reds[entry], greens[entry], blues[entry], alphas[x]);
      }
    };
  } else if (photometric === 2 || photometric === 5) {
    const [first, second, third, black] = channels;
    const cmyk = photometric === 5;
    colourRow = (y, base): void => {
      channels.forEach((into, channel) => readRow(channel, y, into));
      if (alphaChannel >= 0) readRow(alphaChannel, y, alphas);
      if (cmyk) {
        for (let x = 0; x < width; x += 1) {
          const white = 255 - black[x];
          put(base + x, ((255 - first[x]) * white) / 255, ((255 - second[x]) * white) / 255, ((255 - third[x]) * white) / 255, alphas[x]);
        }
      } else {
        for (let x = 0; x < width; x += 1) put(base + x, first[x], second[x], third[x], alphas[x]);
      }
    };
  } else {
    // Grey, black-and-white, or a palette with no palette: one sample, 0 for black unless said to be white.
    const [grey] = channels;
    const invert = photometric === 0;
    colourRow = (y, base): void => {
      readRow(0, y, grey);
      if (invert) for (let x = 0; x < width; x += 1) grey[x] = 255 - grey[x];
      if (alphaChannel >= 0) {
        readRow(alphaChannel, y, alphas);
        for (let x = 0; x < width; x += 1) put(base + x, grey[x], grey[x], grey[x], alphas[x]);
      } else {
        for (let x = 0; x < width; x += 1) bandWords[base + x] = greys[grey[x]];
      }
    };
  }

  const progressRows = Math.max(1, Math.floor(1_000_000 / width));
  for (let y = 0; y < height; y += 1) {
    const row = y % bandRows;
    colourRow(y, row * width);
    if (row === bandRows - 1 || y === height - 1) placeBand(y - row, row + 1);
    if (y % progressRows === progressRows - 1) progress();
  }

  progress();
  return { width: placement.width, height: placement.height, pixels };
};
